// Årsskifte: årets tal, hvor pengene kom fra, og et valg mellem to gaver.
// Solo venter skærmen på et valg (spillet står stille). Online og med Volt vælges den første efter
// en nedtælling, fordi rummet ikke kan vente på én spiller. Tallene kommer fra `year_end`-hændelsen,
// fordi sim'en nulstiller årets statistik i samme tick.

import { PACKS } from '../../content/packs';
import type { DarkCause, PackId, PlayerId, State, YearSummary } from '../../sim';
import { check, greenGoal, greenPct, packDecider, TICK_HZ, YEAR_TICKS } from '../../sim';
import { netOf } from '../../sim/cities';
import { spareProducer } from '../../ai/truth';
import { autoPackIndex, bothPrice, packUseful, yearendAutoTicks } from '../../sim/economy';
import { newKinds } from '../../sim/events';
import { levelOf } from '../../sim/state';
import { h, setText, toggle } from '../dom';
import { ICON } from '../icons';
import { kr, THREAT_NAMES } from '../text';
import { ROLE } from './room';

export interface YearEndView {
  el: HTMLElement;
  update(state: State): void;
}

/**
 * Overskrift efter året. Den følger forsyningen: et godt år kræver, at alle byer har strøm.
 * Mørklægning (tabte hjerter) gør året hårdt. Byer uden strøm uden tabte hjerter gør det travlt:
 * ikke godt, men heller ikke en dom, når man ikke har mistet noget (rådet under tallene siger hvorfor).
 */
export function yearHeadline(stats: Pick<YearSummary, 'blackouts' | 'cities' | 'lit'> & { shaky?: string[] }): string {
  if (stats.blackouts > 0) return 'HÅRDT ÅR';
  if (stats.cities > 0 && stats.lit === 0) return 'STILLE ÅR';
  // Byer, der manglede strøm et stykke tid i året, gør det travlt, også når alle er tændt nu.
  if (stats.lit < stats.cities || (stats.shaky?.length ?? 0) > 0) return 'TRAVLT ÅR';
  return 'GODT ÅR';
}

function names(list: string[]): string {
  if (list.length <= 1) return list[0] ?? '';
  return `${list.slice(0, -1).join(', ')} og ${list[list.length - 1]}`;
}

/**
 * Hvem sad i mørket, og ét råd til næste år ud fra den vigtigste årsag. En by, der får noget men
 * ikke nok, siges med procent: "uden strøm" om en by på 70 % var forkert (playtest).
 */
export function yearDarkText(state: State, stats: Pick<YearSummary, 'dark'> & { shaky?: string[] }): string {
  const nm = (id: string): string => state.nodes.find((n) => n.id === id)?.name ?? id;
  if (stats.dark.length === 0) {
    const shaky = stats.shaky ?? [];
    if (shaky.length === 0) return '';
    return shakyText(state, shaky);
  }
  const out = stats.dark.filter((d) => !d.servedPct);
  const low = stats.dark.filter((d) => d.servedPct);
  const said: string[] = [];
  if (out.length > 0) said.push(`${names(out.map((d) => nm(d.node)))} er uden strøm ved årsskiftet.`);
  if (low.length === 1) said.push(`${nm(low[0]!.node)} får kun ${low[0]!.servedPct} % af sin strøm.`);
  else if (low.length > 1) said.push(`${names(low.map((d) => nm(d.node)))} får ikke strøm nok.`);
  const pick = (c: DarkCause): { node: string } | undefined => stats.dark.find((d) => d.cause === c);
  let tip = '';
  const un = pick('unconnected');
  const cut = pick('cut');
  const lines = pick('lines');
  const power = pick('power');
  if (un && state.lines.some((l) => l.a === un.node || l.b === un.node)) tip = `${nm(un.node)} er ikke forbundet til en mølle eller et værk. Forbind byens net med resten.`;
  else if (un) tip = `Træk en ledning til ${nm(un.node)}.`;
  else if (cut) tip = 'En knækket ledning holdt lyset ude. Hyr en montør mere, så går det hurtigere.';
  else if (lines) tip = `Opgradér ledningen ind til ${nm(lines.node)}, eller byg en station.`;
  else if (power) tip = powerTip(state, power.node);
  return `${said.join(' ')} ${tip}`.trim();
}

/** Det, årsskiftet sagde om byer, der manglede strøm et stykke tid, år for år (pr. bane og seed). */
const shakySaid = new Map<string, Map<number, { tip: number; text: string }>>();

/**
 * Byer, der manglede strøm et stykke tid, men har strøm nu. Rådet følger nettet, som det står: én vej
 * ind, en ledning ind der står næsten fuld, eller for lidt strøm til toppen. Spor: tillid (IGN runde
 * 4: "Odense manglede strøm et stykke tid i år. To veje ind ..." tre år i træk, også da Odense havde
 * tre veje ind). Er rådet det samme som sidste år, kommer det næste, der passer, og står der ikke
 * andet, siges det kort, at det er det samme råd.
 */
function shakyText(state: State, shaky: string[]): string {
  const nm = (id: string): string => state.nodes.find((n) => n.id === id)?.name ?? id;
  const who = names(shaky.map(nm));
  const first = shaky[0] as string;
  const into = state.lines.filter((l) => l.status === 'ok' && (l.a === first || l.b === first));
  const tips: string[] = [];
  if (into.length <= 1) tips.push(`To veje ind til ${nm(first)} holder lyset, også når en ledning knækker.`);
  const tight = into.filter((l) => l.loadPm >= 800).sort((a, b) => b.loadPm - a.loadPm)[0];
  if (tight) {
    const other = nm(tight.a === first ? tight.b : tight.a);
    tips.push(`Ledningen ${nm(first)}–${other} stod næsten fuld. En opgradering giver luft til toppen.`);
  }
  if (levelOf(state).mechanics.includes('battery')) tips.push(`Et batteri ved ${nm(first)} gemmer strøm til toppen.`);
  tips.push('Kobl en mølle mere på før toppen; vinden giver ikke det samme hele året.');
  const run = `${state.levelId}:${state.seed}`;
  let said = shakySaid.get(run);
  if (!said || said.has(state.year + 1)) {
    said = new Map();
    shakySaid.set(run, said);
  }
  const now = said.get(state.year);
  if (now) return now.text;
  const before = said.get(state.year - 1);
  let tip = 0;
  let same = false;
  if (before) {
    const at = tips.indexOf(before.text.slice(before.text.indexOf('. ') + 2));
    if (at >= 0) tip = (at + 1) % tips.length;
    same = at >= 0 && tip === at;
  }
  const again = before?.text.startsWith(who) ? 'igen ' : '';
  const text = `${who} manglede strøm ${again}et stykke tid i år. ${same ? 'Samme råd som sidste år.' : tips[tip]}`;
  said.set(state.year, { tip, text });
  return text;
}

/**
 * Byen manglede produktion: sig, hvad der faktisk ville have hjulpet. En ledig producent uden for
 * byens net nævnes ved navn; kører værket allerede, er "brug et værk" et forkert råd (playtest runde 3).
 */
function powerTip(state: State, node: string): string {
  const net = netOf(state, node);
  // Spor "sandhed": kun en producent, en ledning faktisk kan nå.
  const other = spareProducer(state, node);
  if (other) return `${other.name} havde strøm til overs. En ledning dertil kan dække toppen.`;
  const plant = state.nodes.some((m) => m.producer && m.active && net.has(m.id) && m.producer.green < 0.5 && m.kind !== 'foreign');
  if (plant) return 'Alt, der var koblet på, kørte fuldt, også værket. Kobl flere møller på, eller byg et batteri til toppen.';
  return 'Vind og sol rakte ikke. Kobl en mølle mere på, eller brug et værk som reserve.';
}

/**
 * Årets penge som et regnestykke, der går op: hvert led i kroner og summen lig det store tal.
 * Playtest runde 3: "12 kr tilskud · +7 kr til vækst" under "+18 kr" fik en tester til at regne efter.
 */
export function moneyLine(stats: Pick<YearSummary, 'baseGrant' | 'powerKr' | 'earlyKr' | 'grant' | 'advance'>, total: number): string {
  const parts: string[] = [];
  const add = (n: number | undefined, what: string): void => {
    if (n && n > 0) parts.push(`${kr(n)} ${what}`);
  };
  add(stats.baseGrant, 'tilskud');
  add(stats.powerKr, 'for strømmen');
  add(stats.earlyKr, 'for tidligt kald');
  add(stats.grant, 'til vækst');
  const sum = parts.length > 1 ? `${parts.join(' + ')} = ${kr(total)}.` : `${parts[0] ?? kr(total)}.`;
  const already = stats.advance + stats.grant;
  if (already <= 0) return sum;
  if (already >= total) return `${sum} Det kom ind løbende i løbet af året.`;
  return `${sum} ${kr(already)} af dem kom allerede i løbet af året.`;
}

/**
 * Den grønne andel som håndtag (IGN runde 2: "grøn faldt fra 61 til 50 %, og årsskiftet sagde GODT
 * ÅR"). Under målet: andelen, målet og det ene træk, der løfter mest. Over målet: en kort ros.
 */
export function greenLine(state: State): { text: string; low: boolean } | null {
  if (state.stats.servedSum <= 0) return null;
  const g = Math.round(greenPct(state));
  const goal = greenGoal(levelOf(state).greenGoalPct, state.difficulty);
  if (g >= goal) return { text: `Grøn ${g} % · mål ${goal} %. Den grønne stjerne er hjemme, hvis ${state.coop ? 'I' : 'du'} holder den.`, low: false };
  const served = Math.max(1, state.flow.servedMw);
  // En grøn producent uden ledning løfter mest: navngiv den med det, den kan give.
  const lonely = state.nodes
    .filter((m) => m.producer && m.active && !m.phantom && m.kind !== 'foreign' && m.producer.green >= 0.5 && !state.lines.some((l) => l.a === m.id || l.b === m.id))
    .sort((a, b) => (b.producer?.availMw ?? 0) - (a.producer?.availMw ?? 0))[0];
  let tip = 'Et batteri gemmer vind og sol til toppen, og en grøn ledning mere hjælper.';
  if (lonely?.producer) {
    const up = Math.max(1, Math.min(goal - g, Math.round((lonely.producer.availMw * 100) / served)));
    tip = `Forbind ${lonely.name} (+${up} %).`;
  }
  return { text: `Grøn ${g} % · mål ${goal} %. ${tip}`, low: true };
}

/**
 * Næste år i én linje: hvem der dukker op, og hvilken by der hænger i én ledning. IGN-løkke runde 3:
 * "Orkan bane 1 år 2 er en mur": Vinderup kom uden varsel, og Holstebro voksede ind i vinteren.
 */
export function nextYearText(state: State): string {
  const until = state.clockTick + YEAR_TICKS;
  const nm = (id: string): string | null => {
    const n = state.nodes.find((m) => m.id === id);
    return n && (n.kind === 'city' || n.kind === 'datacenter' || n.producer) ? n.name : null;
  };
  const coming: string[] = [];
  for (const s of state.pendingSites) if (s.at < until) coming.push(s.node);
  state.pool.forEach((id, i) => {
    const at = i === 0 ? state.nextCustomerAt : (state.customerPlan[i] ?? Number.MAX_SAFE_INTEGER);
    if (at < until) coming.push(id);
  });
  const who = [...new Set(coming)].map(nm).filter((x): x is string => !!x).slice(0, 3);
  const parts: string[] = [];
  if (who.length > 0) parts.push(`Næste år kommer ${names(who)}.`);
  // Den største by med strøm og kun én vej ind: den rammer vinteren først.
  const lone = state.nodes
    .filter((n) => n.city && n.kind === 'city' && n.active && !n.phantom && n.shield <= 1 && state.lines.some((l) => l.a === n.id || l.b === n.id))
    .sort((a, b) => (b.city?.demandMw ?? 0) - (a.city?.demandMw ?? 0))[0];
  // "Én ledning" kun, når det er sandt: har byen to ledninger, der hænger på samme sted, er det ét brud.
  if (lone) {
    const own = state.lines.filter((l) => l.a === lone.id || l.b === lone.id).length;
    parts.push(own <= 1 ? `${lone.name} vokser og hænger i én ledning: giv den vej nr. 2 før vinteren.` : `${lone.name} vokser, og ét brud kan gøre den mørk: giv den en vej mere før vinteren.`);
  }
  return parts.join(' ');
}

/**
 * Det nye i næste år (spor "sværhed"): hvert år låser mindst én ny slags hændelse op, og kortet
 * siger hvilken, før den kommer. Finalen har det hele.
 */
export function newsText(state: State): string | null {
  const level = levelOf(state);
  if (!level.news) return null;
  const next = state.year + 1;
  if (next >= level.years) return 'Næste år er finalen: alt kan komme, også på én gang.';
  const names = newKinds(level, next)
    .map((k) => THREAT_NAMES[k])
    .filter((x): x is string => !!x);
  return names.length > 0 ? `Nyt næste år: ${names.join(' og ')}.` : null;
}

/** Står et mørkeur og tikker, siger kortet, at det venter: valget koster ikke byen (nytårsfristen). */
export function clockWaitText(state: State): string | null {
  const ticking = state.nodes.some((n) => n.active && !n.phantom && n.city && n.city.clock > 0 && n.city.darkTicks === 0);
  return ticking ? 'Mørkeurene står stille, mens du vælger, og det nye år giver byerne en frist.' : null;
}

function packIcon(id: PackId): string {
  if (id.includes('crew')) return ICON.crew;
  if (id.includes('battery') || id === 'flywheel') return ICON.bolt;
  if (id.includes('cable')) return ICON.bury;
  // Hver gave sit eget ikon (playtest runde 2: Borgermøde stod med ledningsikonet).
  if (id === 'town_meeting') return ICON.heart;
  if (id === 'foreign_plus50') return ICON.target;
  if (id === 'free_double') return ICON.upgrade;
  if (id === 'free_station') return ICON.station;
  return ICON.line;
}

export function yearEndScreen(
  state: State,
  year: number,
  stats: YearSummary,
  pick: (i: number) => void,
  opts: { timed?: boolean; you?: PlayerId; both?: () => void; bid?: string } = {},
): YearEndView {
  const timed = opts.timed ?? true;
  // Makkerspil med roller (IGN-løkke runde 4): gaven vælges i to trin, og hver stemme vises på kortet.
  const vote = !!state.roles && opts.you !== undefined;
  const you: PlayerId = opts.you ?? 'p1';
  const decider = ROLE[packDecider(state)].name;
  // Et tryk lige efter skærmen dukker op var ment til kortet bagved: det vælger ikke en gave.
  const shownAt = performance.now();
  const bar = h('i', { class: 'bar-fill' });
  const left = h('span', {});
  const head = yearHeadline(stats);
  // IGN-løkke 2, runde 2: mærket "Vælges af sig selv" stod altid på første gave, men uret tager den
  // gave, der er mest værd (autoPackIndex). Med Volt som makker er det også hans bud ("bid").
  const auto = autoPackIndex(state, levelOf(state));
  const cards = state.packOffer.map((id, i) =>
    h(
      'button',
      {
        type: 'button',
        class: `pack${i === auto && timed && !vote ? ' first' : ''}`,
        onclick: () => {
          // 900 ms: et tryk fra spillet, der lander på kortet, vælger ikke (IGN runde 4: 450 ms var for kort).
          if (performance.now() - shownAt > 900) pick(i);
        },
      },
      h('span', { class: 'kbd', 'aria-hidden': 'true' }, String(i + 1)),
      h('span', { class: 'pack-icon', html: packIcon(id) }),
      h('b', {}, PACKS[id].name),
      h('span', {}, PACKS[id].text),
      // En gave, der ikke kan bruges endnu, gemmes (IGN runde 1: "Dobbelt 400 kV" uden en eneste 400 kV).
      packUseful(state, levelOf(state), id) ? null : h('span', { class: 'pack-later' }, 'Gemmes, til du kan bruge den'),
      i === auto && !vote && (timed || opts.bid)
        ? h('span', { class: 'pack-default' }, opts.bid ? `${opts.bid}s bud${timed ? ' · vælges af sig selv' : ''}` : 'Vælges af sig selv')
        : null,
      vote ? h('span', { class: 'pack-votes', 'aria-live': 'polite' }) : null,
    ),
  );
  // Begge gaver for en pris, der stiger år for år (spor "sværhed": kassens sene dræn).
  const price = bothPrice(state.year);
  const canBoth = check(state, 'pick_both', undefined, undefined, you).ok;
  const both =
    opts.both && state.packOffer.length >= 2
      ? h(
          'button',
          {
            type: 'button',
            class: 'pack-both',
            disabled: !canBoth,
            'aria-label': canBoth ? `Tag begge gaver for ${kr(price)}` : `Begge gaver koster ${kr(price)}. Du mangler ${kr(price - state.budget)}`,
            onclick: () => {
              if (performance.now() - shownAt > 900) opts.both?.();
            },
          },
          h('span', { class: 'kbd', 'aria-hidden': 'true' }, '3'),
          h('span', { class: 'pack-icon', html: ICON.coin }),
          h('b', { 'aria-hidden': 'true' }, 'Tag begge'),
          h('span', { class: 'pack-both-price', 'aria-hidden': 'true' }, canBoth ? kr(price) : `${kr(price)} · mangler ${kr(price - state.budget)}`),
        )
      : null;
  const news = newsText(state);
  const waits = clockWaitText(state);
  // Kort om pengene: én linje. Aldrig MW i UI'et (DESIGN.md): væksten fortælles i kroner.
  const why = moneyLine(stats, state.lastIncome) + (stats.repairKr ? ` Reparationer kostede ${kr(stats.repairKr)}; forstærkede master knækker ikke.` : '');
  const darkText = yearDarkText(state, stats);
  const gl = greenLine(state);
  // Sidste årsskifte før finalen: der kommer ikke flere byer, men en by i én ledning er stadig værd at sige.
  const ahead = nextYearText(state);
  // IGN-løkke 2: gaverne først. Kortet var 845 px højt i et vindue på 800, og "Tag begge" lå under
  // kanten. Overskrift, tal og årets vigtigste råd står fremme; resten (pengene, grøn andel, næste
  // år) er foldet sammen under gaverne, ét tryk væk.
  const more = [
    gl ? h('p', { class: `why green-tip${gl.low ? ' low' : ''}` }, h('span', { class: 'green-tip-icon', html: ICON.leaf }), h('span', {}, gl.text)) : null,
    h('p', { class: 'why' }, why),
    ahead ? h('p', { class: 'why next-year' }, h('span', { class: 'green-tip-icon', html: ICON.target }), h('span', {}, ahead)) : null,
    news ? h('p', { class: 'why next-year news' }, h('span', { class: 'green-tip-icon', html: ICON.bolt }), h('span', {}, news)) : null,
  ];
  const el = h(
    'div',
    { class: `card yearend${head === 'HÅRDT ÅR' ? ' hard' : ''}` },
    h(
      'div',
      { class: 'yearend-top' },
      h('p', { class: 'eyebrow' }, `Årsskifte · ${year}`),
      h('h1', { class: 'title small' }, head),
      h(
        'div',
        { class: 'figures' },
        h('div', { class: 'figure' }, h('b', {}, `+${kr(state.lastIncome)}`), h('span', {}, 'tjent i år')),
        h('div', { class: `figure${stats.shaky.length > 0 && stats.lit === stats.cities ? ' warn' : ''}` }, h('b', {}, `${stats.lit}/${stats.cities}`), h('span', {}, stats.shaky.length > 0 ? 'med strøm nu' : 'byer med strøm')),
        h('div', { class: `figure${stats.blackouts > 0 ? ' bad' : ''}` }, h('b', {}, String(stats.blackouts)), h('span', {}, stats.blackouts === 1 ? 'hjerte tabt' : 'hjerter tabt')),
      ),
      darkText ? h('p', { class: 'why dark-tip' }, darkText) : null,
    ),
    h(
      'div',
      { class: 'yearend-pick' },
      h('h2', { class: 'section' }, vote ? 'Vælg én gave sammen' : 'Vælg én gave'),
      vote ? h('p', { class: 'why vote-rule' }, `Tryk på den samme, så er I enige. Er I uenige, afgør ${decider}en i år.`) : null,
      h('div', { class: 'packs' }, ...cards),
      both,
      waits ? h('p', { class: 'why next-year clock-wait' }, h('span', { class: 'green-tip-icon', html: ICON.clock }), h('span', {}, waits)) : null,
      timed ? h('p', { class: 'countdown' }, h('span', { class: 'bar', 'aria-hidden': 'true' }, bar), left) : null,
    ),
    h('details', { class: 'yearend-more' }, h('summary', {}, news ? 'Pengene, grøn strøm og næste år' : 'Pengene og grøn strøm'), ...more),
  );
  return {
    el,
    update(s: State): void {
      if (!timed) return;
      const auto = yearendAutoTicks(s);
      const f = Math.max(0, 1 - s.phaseTicks / auto);
      bar.style.transform = `scaleX(${f.toFixed(3)})`;
      const secs = Math.ceil((auto - s.phaseTicks) / TICK_HZ);
      if (!vote) {
        setText(left, `Vælges af sig selv om ${secs} s`);
        return;
      }
      const other: PlayerId = you === 'p1' ? 'p2' : 'p1';
      const v = s.packVotes ?? {};
      cards.forEach((c, i) => {
        const mine = v[you] === i;
        const theirs = v[other] === i;
        toggle(c, 'voted', mine);
        toggle(c, 'partner-voted', theirs);
        const tag = c.querySelector('.pack-votes');
        if (tag) setText(tag, mine && theirs ? 'I er enige' : mine ? 'Din stemme' : theirs ? 'Makker stemmer her' : '');
      });
      const mineIn = v[you] !== undefined;
      const theirsIn = v[other] !== undefined;
      setText(
        left,
        theirsIn && !mineIn
          ? `Makker har stemt. Enig? Tryk på samme gave. ${secs} s`
          : mineIn && !theirsIn
            ? `Venter på makker. Din gave tages om ${secs} s`
            : `Vælges af sig selv om ${secs} s`,
      );
    },
  };
}
